/* Проверки загрузки сервера без React (engine-kit, план M3 §2 S2): GET /app/uploads/{id} (docs/03-api §8.4) — статус, проверки снимка
   (лицо, резкость, повтор, один человек, размер), предупреждения, причина отказа и то, что сервер прочитал в файле (media: длительность,
   размеры после поворота, есть ли звук). Ничего не выдумываем на клиенте: нет ответа — нет проверки. Хук useUploadChecks
   (shoot/kit/use-upload-checks.ts) — тонкая обёртка над loadUploadChecks; движки читают те же функции через мост загрузок (media-bridge). */
import { endpoints } from '../../../api';
import type { UploadStatusResponse } from '../../../api/types';
import { safeMediaUrl } from '../studio-content';

export interface CheckItem {
  key: string;
  ok: boolean;
  /** Статус проверки сервера: passed · warning · not_checked · failed */
  status?: string;
}
/** Что сервер прочитал в файле (docs/03-api §8.4 media); неизвестное поле — null */
export interface UploadMedia {
  durationMs: number | null;
  width: number | null;
  height: number | null;
  hasAudio: boolean | null;
}
export interface UploadCheck {
  status: UploadStatusResponse['status'];
  checks: CheckItem[];
  warnings: string[];
  reason: string | null;
  /** Есть, только если сервер прислал media */
  media?: UploadMedia;
  /** У product_photo — товар без фона (только безопасный адрес); есть, только если сервер его прислал */
  cutout?: string;
}

/** Строка проверки сервера. Контракт docs/59 (§avatar): `{key, status, value}` — `passed`/`warning`, у детектора лиц
    `not_checked`; прежняя форма — `{key, ok}`. Без нормализации `ok` был undefined, и каждый снимок «Своего аватара»
    на стенде считался «Нечётко» (живой прогон). Провал — только явный: `ok: false` или статус failed/rejected. */
export function checkItem(raw: unknown): CheckItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as { key?: unknown; ok?: unknown; status?: unknown };
  if (typeof item.key !== 'string') return null;
  const status = typeof item.status === 'string' ? item.status : undefined;
  const ok = typeof item.ok === 'boolean' ? item.ok : !['failed', 'rejected', 'error'].includes(status ?? '');
  return { key: item.key, ok, ...(status ? { status } : {}) };
}
const count = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null);
/** media ответа загрузки; не объект — null */
export function uploadMedia(raw: unknown): UploadMedia | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const m = raw as Record<string, unknown>;
  return {
    durationMs: count(m.duration_ms),
    width: count(m.width),
    height: count(m.height),
    hasAudio: typeof m.has_audio === 'boolean' ? m.has_audio : null,
  };
}
/** Ответ GET /app/uploads/{id} → проверка для экрана */
export function uploadCheck(value: UploadStatusResponse): UploadCheck {
  const media = uploadMedia((value as { media?: unknown }).media);
  const cutout = safeMediaUrl((value as { cutout_url?: unknown }).cutout_url);
  return {
    status: value.status,
    checks: ((value.checks ?? []) as unknown[]).map(checkItem).filter((item): item is CheckItem => !!item),
    warnings: Array.isArray(value.warnings) ? value.warnings.filter((item): item is string => typeof item === 'string') : [],
    reason: value.reject_reason ?? null,
    ...(media ? { media } : {}),
    ...(cutout ? { cutout } : {}),
  };
}
/** UUID загрузок по ключам слотов (только похожие на UUID), в устойчивом порядке — ключ кэша и зависимость хука */
export function uploadIdentity(ids: Record<string, string>): string {
  return JSON.stringify(
    Object.entries(ids)
      .filter(([, id]) => /^[a-f0-9-]{36}$/i.test(id))
      .sort(),
  );
}
/** Проверки нескольких загрузок по ключам: каждая — свой GET; не ответившая не попадает в результат (не выдумываем) */
export async function loadUploadChecks(ids: Record<string, string>, signal?: AbortSignal): Promise<Record<string, UploadCheck>> {
  const entries = JSON.parse(uploadIdentity(ids)) as Array<[string, string]>;
  const results = await Promise.allSettled(entries.map(async ([key, id]) => [key, await endpoints.uploadStatus(id, signal)] as const));
  const checks: Record<string, UploadCheck> = {};
  for (const result of results)
    if (result.status === 'fulfilled') {
      const [key, value] = result.value;
      checks[key] = uploadCheck(value);
    }
  return checks;
}
/** Снимок подходит, если сервер не нашёл в нём проблем. */
export const checkOk = (check: UploadCheck | undefined) =>
  !check || (check.status === 'ready' && check.checks.every(item => item.ok) && !check.reason);
const LABELS: Record<string, string> = {
  face: 'Нет лица',
  sharp: 'Нечётко',
  sharpness: 'Нечётко',
  duplicate: 'Повтор',
  single_person: 'Двое в кадре',
  one_person: 'Двое в кадре',
  resolution: 'Мелкое фото',
  min_side: 'Мелкое фото',
};
export const checkLabel = (check: UploadCheck | undefined) => {
  const failed = check?.checks.find(item => !item.ok);
  return failed ? (LABELS[failed.key] ?? 'Не подойдёт') : check?.reason ? 'Не подойдёт' : '';
};
/** Провалена ли проверка с этим ключом (лицо в кадре кружка — face, человек в ролике — dancer); нет проверки — false */
export const checkFailed = (check: UploadCheck | undefined, key: string) => !!check?.checks.some(item => item.key === key && !item.ok);
